import { FunctionsHttpError } from '@supabase/supabase-js';

/**
 * Error handling for responses from our Supabase edge functions.
 *
 * The trap this module exists for: on a non-2xx response,
 * `supabase.functions.invoke` RESOLVES (it never rejects) with
 * `{ data: null, error: FunctionsHttpError }`, and `error.context` is the raw
 * fetch `Response` — not a parsed body. Code that reads `error.context.code`
 * gets `undefined`, which silently discards the server's error code, message,
 * and Sentry/support ids.
 */

/** Returned by analyze-receipt (HTTP 402) when the server-side credit gate rejects a scan. */
export const INSUFFICIENT_CREDITS_CODE = 'INSUFFICIENT_CREDITS';

/** JSON body our edge functions send on error (see analyze-receipt's catch block). */
export interface EdgeFunctionErrorBody {
  error?: string;
  code?: string;
  sentryEventId?: string;
  supportInfo?: {
    userId?: string;
    userEmail?: string;
    timestamp?: string;
  };
}

/** An Error carrying the server-side details of an edge-function failure. */
export interface EdgeFunctionError extends Error {
  code?: string;
  sentryEventId?: string;
  supportInfo?: EdgeFunctionErrorBody['supportInfo'];
}

/**
 * Read the JSON body of a `FunctionsHttpError`. Returns `{}` for anything else,
 * or when the body is missing or isn't JSON.
 *
 * Reads a clone, so it is safe to call more than once — a Response body can
 * otherwise only be consumed a single time.
 */
export async function readEdgeFunctionErrorBody(error: unknown): Promise<EdgeFunctionErrorBody> {
  if (!(error instanceof FunctionsHttpError)) return {};

  const response = error.context as Response | undefined;
  if (!response || typeof response.clone !== 'function') return {};

  try {
    const body = await response.clone().json();
    return body && typeof body === 'object' ? (body as EdgeFunctionErrorBody) : {};
  } catch {
    return {};
  }
}

/**
 * Normalise the `error` returned by `supabase.functions.invoke` into an Error
 * carrying the server's message, code and support ids, falling back to the
 * transport error's own message when there is no usable body.
 */
export async function toEdgeFunctionError(error: unknown): Promise<EdgeFunctionError> {
  const body = await readEdgeFunctionErrorBody(error);
  const fallbackMessage = error instanceof Error ? error.message : 'Unknown error';

  const normalised = new Error(body.error || fallbackMessage) as EdgeFunctionError;
  normalised.code = body.code ?? (error as { code?: string } | undefined)?.code;
  normalised.sentryEventId = body.sentryEventId;
  normalised.supportInfo = body.supportInfo;
  return normalised;
}

/**
 * True when the server-side credit gate rejected the call. Accepts either the
 * raw `FunctionsHttpError` or an error already normalised by
 * `toEdgeFunctionError`.
 */
export async function isInsufficientCreditsError(error: unknown): Promise<boolean> {
  if ((error as { code?: string } | undefined)?.code === INSUFFICIENT_CREDITS_CODE) return true;
  return (await readEdgeFunctionErrorBody(error)).code === INSUFFICIENT_CREDITS_CODE;
}
